import { Helmet } from "react-helmet-async";

const SITE_URL = "https://restaurantemuseoteneria.es";
const DEFAULT_IMAGE = `${SITE_URL}/og-default.jpg`;

/**
 * SEO — Single source of truth for every page's <head>.
 *
 * One <SEO/> component per page handles:
 *  - <title>, meta description, keywords, canonical, hreflang.
 *  - Robots / Googlebot directives that allow large image previews
 *    (max-image-preview:large) so Google can display proper food / interior photos.
 *  - Geo tags (region, placename, ICBM) for local SEO.
 *  - Open Graph (Facebook / WhatsApp / LinkedIn previews) + Twitter Cards.
 *  - One or more application/ld+json blobs (Restaurant, Menu, FAQPage,
 *    Museum, BlogPosting, BreadcrumbList, ItemList of Award, …) — the engine
 *    decides which rich-result eligibility we trigger per page.
 *
 * Props
 * @param {string}  [title]          — page title (will be suffixed with brand).
 * @param {string}  [description]    — meta description (155–160 chars ideally).
 * @param {string}  [keywords]       — comma-separated; mostly for legacy crawlers.
 * @param {string}  [image]          — absolute or site-rooted OG/Twitter image.
 * @param {string}  [path=""]        — site-rooted path (e.g. "/premios"), used to
 *                                     build canonical + og:url.
 * @param {"website"|"article"|"restaurant"} [type="website"]
 * @param {{name:string,item:string}[]} [breadcrumbs] — optional crumbs.
 * @param {Object|Array<Object>} [jsonLd] — extra Schema.org objects to inject.
 * @param {string}  [publishedTime]  — ISO 8601, only used when type="article".
 * @param {string}  [modifiedTime]   — ISO 8601, only used when type="article".
 * @param {string}  [author]         — used in og:article:author + meta.
 *
 * Implementation notes
 *  - We always link x-default + es-ES because the site is monolingual today,
 *    but the structure is ready for EN/FR alternates without breaking SEO.
 *  - When SEO is rendered inside a `<HelmetProvider>` (set up in App.js), tags
 *    are deduped automatically — multiple SEO components on the same page would
 *    cause unpredictable order, so each page should mount exactly one.
 */
export default function SEO({
    title,
    description,
    keywords,
    image = DEFAULT_IMAGE,
    path = "",
    type = "website",
    breadcrumbs,
    jsonLd,
    publishedTime,
    modifiedTime,
    author = "Restaurante Museo Tenería",
}) {
    const fullTitle = title
        ? `${title} · Restaurante Museo Tenería · Ocaña`
        : "Restaurante Museo Tenería · Ocaña Toledo · Edificio del siglo XV";
    const desc =
        description ||
        "Restaurante Museo Tenería en Ocaña, Toledo. Edificio único del siglo XV, cocina tradicional manchega, parrilla premiada. Nº1 TripAdvisor Ocaña.";
    const url = `${SITE_URL}${path}`;
    // Image may arrive as "/og-foo.jpg" or as a full URL — normalise.
    const ogImage = image.startsWith("http") ? image : `${SITE_URL}${image}`;

    // BreadcrumbList JSON-LD — Google can render the chain in the SERP.
    const breadcrumbLd = breadcrumbs && {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((b, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: b.name,
            item: `${SITE_URL}${b.item}`,
        })),
    };

    const allJsonLd = [];
    if (breadcrumbLd) allJsonLd.push(breadcrumbLd);
    if (Array.isArray(jsonLd)) allJsonLd.push(...jsonLd);
    else if (jsonLd) allJsonLd.push(jsonLd);

    return (
        <Helmet>
            <title>{fullTitle}</title>
            <meta name="description" content={desc} />
            {keywords && <meta name="keywords" content={keywords} />}
            <link rel="canonical" href={url} />
            <link rel="alternate" hrefLang="es-ES" href={`${url}?lng=es`} />
            <link rel="alternate" hrefLang="en" href={`${url}?lng=en`} />
            <link rel="alternate" hrefLang="fr" href={`${url}?lng=fr`} />
            <link rel="alternate" hrefLang="x-default" href={url} />
            <meta
                name="robots"
                content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
            />
            <meta name="googlebot" content="index, follow" />
            <meta name="author" content={author} />
            <meta name="language" content="Spanish" />
            <meta httpEquiv="content-language" content="es-ES" />
            <meta name="theme-color" content="#1A0D0E" />

            {/* Geo tags — Ocaña, Toledo */}
            <meta name="geo.region" content="ES-TO" />
            <meta name="geo.placename" content="Ocaña, Toledo" />
            <meta name="geo.position" content="39.9573;-3.5024" />
            <meta name="ICBM" content="39.9573, -3.5024" />

            {/* Open Graph */}
            <meta property="og:type" content={type} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={desc} />
            <meta property="og:url" content={url} />
            <meta property="og:image" content={ogImage} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:image:alt" content={fullTitle} />
            <meta property="og:site_name" content="Restaurante Museo Tenería" />
            <meta property="og:locale" content="es_ES" />
            {publishedTime && <meta property="article:published_time" content={publishedTime} />}
            {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
            {publishedTime && <meta property="article:author" content={author} />}

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={desc} />
            <meta name="twitter:image" content={ogImage} />
            <meta name="twitter:image:alt" content={fullTitle} />

            {/* JSON-LD scripts */}
            {allJsonLd.map((obj, i) => (
                <script type="application/ld+json" key={i}>
                    {JSON.stringify(obj)}
                </script>
            ))}
        </Helmet>
    );
}
